import { useEffect, useState, useMemo } from "react"
import {
  Container,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  LinearProgress,
  Stack,
  Card,
  CardContent,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import GpsFixedIcon from "@mui/icons-material/GpsFixed"
import GpsOffIcon from "@mui/icons-material/GpsOff"
import SignalWifiStatusbar4BarIcon from "@mui/icons-material/SignalWifiStatusbar4Bar"
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import AppApi from "../utils/AppApi"
import moment from "moment"

// Threshold in minutes
const THRESHOLD_OK = 10
const THRESHOLD_WARNING = 30
const THRESHOLD_DANGER = 60

const getGpsTime = (truck) => {
  return truck.dateTime || null
}

const getStatusInfo = (diffMinutes) => {
  if (diffMinutes === null)
    return { label: "ไม่มีข้อมูล", color: "default", severity: 3 }
  if (diffMinutes <= THRESHOLD_OK)
    return { label: "ปกติ", color: "success", severity: 0 }
  if (diffMinutes <= THRESHOLD_WARNING)
    return { label: "ล่าช้า", color: "warning", severity: 1 }
  if (diffMinutes <= THRESHOLD_DANGER)
    return { label: "ผิดปกติ", color: "error", severity: 2 }
  return { label: "ขาดการติดต่อ", color: "error", severity: 3 }
}

const formatTimeDiff = (diffMinutes) => {
  if (diffMinutes === null) return "-"
  if (diffMinutes < 1) return "เมื่อสักครู่"
  if (diffMinutes < 60) return `${diffMinutes} นาที`
  const hours = Math.floor(diffMinutes / 60)
  const mins = diffMinutes % 60
  if (hours < 24) return `${hours} ชม. ${mins} นาที`
  const days = Math.floor(hours / 24)
  const remainHours = hours % 24
  return `${days} วัน ${remainHours} ชม.`
}

const GpsMonitor = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [updated, setUpdated] = useState(null)
  const [search, setSearch] = useState("")
  const [orderBy, setOrderBy] = useState("diff")
  const [order, setOrder] = useState("desc")
  const [now, setNow] = useState(moment())
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const fetchData = async () => {
    setLoading(true)
    try {
      const result = await AppApi.get("/truck_location")
      setData(result.data)
      setUpdated(moment())
    } catch (err) {
      console.error("Error fetching truck status:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [])

  // Update "now" every 30 seconds to keep time diffs fresh
  useEffect(() => {
    const timer = setInterval(() => setNow(moment()), 30000)
    return () => clearInterval(timer)
  }, [])

  const enrichedData = useMemo(() => {
    return data.map((truck) => {
      const gpsTime = getGpsTime(truck)
      const diffMinutes = gpsTime ? now.diff(moment(gpsTime), "minutes") : null
      const status = getStatusInfo(diffMinutes)
      return { ...truck, gpsTime, diffMinutes, status }
    })
  }, [data, now])

  const summaryStats = useMemo(() => {
    const total = enrichedData.length
    const ok = enrichedData.filter((t) => t.status.severity === 0).length
    const warning = enrichedData.filter((t) => t.status.severity === 1).length
    const danger = enrichedData.filter((t) => t.status.severity === 2).length
    const offline = enrichedData.filter((t) => t.status.severity === 3).length
    return { total, ok, warning, danger, offline }
  }, [enrichedData])

  const filteredAndSorted = useMemo(() => {
    let filtered = enrichedData
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      filtered = enrichedData.filter(
        (t) =>
          t.vehicleName?.toLowerCase().includes(q) ||
          t.address?.toLowerCase().includes(q),
      )
    }

    return filtered.sort((a, b) => {
      let aVal, bVal
      switch (orderBy) {
        case "vehicleName":
          aVal = a.vehicleName || ""
          bVal = b.vehicleName || ""
          return order === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal)
        case "gpsTime":
          aVal = a.gpsTime ? moment(a.gpsTime).valueOf() : 0
          bVal = b.gpsTime ? moment(b.gpsTime).valueOf() : 0
          return order === "asc" ? aVal - bVal : bVal - aVal
        case "diff":
          aVal = a.diffMinutes ?? 999999
          bVal = b.diffMinutes ?? 999999
          return order === "asc" ? aVal - bVal : bVal - aVal
        case "status":
          aVal = a.status.severity
          bVal = b.status.severity
          return order === "asc" ? aVal - bVal : bVal - aVal
        case "speed":
          aVal = a.speed ?? 0
          bVal = b.speed ?? 0
          return order === "asc" ? aVal - bVal : bVal - aVal
        default:
          return 0
      }
    })
  }, [enrichedData, search, orderBy, order])

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === "asc" ? "desc" : "asc")
    } else {
      setOrderBy(column)
      setOrder(column === "diff" || column === "status" ? "desc" : "asc")
    }
  }

  return (
    <Container
      maxWidth="xl"
      sx={{ py: { xs: 1.5, md: 3 }, px: { xs: 1, sm: 2, md: 3 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight={700}
            color="text.primary"
          >
            GPS Monitor
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {isMobile
              ? updated && `อัพเดต: ${updated.format("HH:mm:ss")}`
              : `ระบบติดตามสถานะ GPS แบบ Real-Time${updated ? ` — อัพเดตล่าสุด: ${updated.format("DD/MM/YYYY HH:mm:ss")}` : ""}`}
          </Typography>
        </Box>
        <Tooltip title="รีเฟรชข้อมูล">
          <IconButton
            onClick={fetchData}
            color="primary"
            size={isMobile ? "medium" : "large"}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(5, 1fr)",
          },
          gap: { xs: 1, md: 2 },
          mb: { xs: 2, md: 3 },
        }}
      >
        <SummaryCard
          title="ทั้งหมด"
          value={summaryStats.total}
          color="#1976d2"
          icon={<GpsFixedIcon />}
          compact={isMobile}
        />
        <SummaryCard
          title="ปกติ"
          subtitle={`≤ ${THRESHOLD_OK} นาที`}
          value={summaryStats.ok}
          color="#2e7d32"
          icon={<SignalWifiStatusbar4BarIcon />}
          compact={isMobile}
        />
        <SummaryCard
          title="ล่าช้า"
          subtitle={`${THRESHOLD_OK}–${THRESHOLD_WARNING} นาที`}
          value={summaryStats.warning}
          color="#ed6c02"
          icon={<WarningAmberIcon />}
          compact={isMobile}
        />
        <SummaryCard
          title="ผิดปกติ"
          subtitle={`${THRESHOLD_WARNING}–${THRESHOLD_DANGER} นาที`}
          value={summaryStats.danger}
          color="#d32f2f"
          icon={<GpsOffIcon />}
          compact={isMobile}
        />
        <SummaryCard
          title="ขาดการติดต่อ"
          subtitle={`> ${THRESHOLD_DANGER} นาที`}
          value={summaryStats.offline}
          color="#616161"
          icon={<SignalWifiOffIcon />}
          compact={isMobile}
        />
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="ค้นหาทะเบียนรถ หรือ ที่อยู่..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth={isMobile}
          sx={{ width: isMobile ? "100%" : 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          component={isMobile ? "div" : "span"}
          sx={{ ml: isMobile ? 0 : 2, mt: isMobile ? 0.5 : 0 }}
        >
          แสดง {filteredAndSorted.length} จาก {enrichedData.length} คัน
        </Typography>
      </Box>

      {/* Mobile: Card View */}
      {isMobile ? (
        <Stack spacing={1}>
          {filteredAndSorted.map((truck, idx) => (
            <MobileCard key={truck.vehicleName} truck={truck} index={idx} />
          ))}
          {filteredAndSorted.length === 0 && !loading && (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">ไม่พบข้อมูล</Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        /* Desktop: Table View */
        <TableContainer
          component={Paper}
          elevation={1}
          sx={{ borderRadius: 2, maxHeight: "calc(100vh - 380px)" }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>#</TableCell>
                <SortableHeader
                  label="ทะเบียนรถ"
                  column="vehicleName"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="สถานะ GPS"
                  column="status"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="GPS อัพเดตล่าสุด"
                  column="gpsTime"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="ห่างจากปัจจุบัน"
                  column="diff"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="ความเร็ว (km/h)"
                  column="speed"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                />
                <TableCell sx={headerCellStyle}>ตำแหน่ง</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSorted.map((truck, idx) => (
                <TableRow
                  key={truck.vehicleName}
                  hover
                  sx={{
                    backgroundColor:
                      truck.status.severity >= 3
                        ? "rgba(211, 47, 47, 0.04)"
                        : truck.status.severity === 2
                          ? "rgba(211, 47, 47, 0.02)"
                          : "inherit",
                    "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.04)" },
                  }}
                >
                  <TableCell sx={{ color: "text.secondary", width: 50 }}>
                    {idx + 1}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.9rem">
                      {truck.vehicleName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={truck.status.label}
                      color={truck.status.color}
                      size="small"
                      variant={
                        truck.status.severity >= 2 ? "filled" : "outlined"
                      }
                      sx={{ fontWeight: 500, minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {truck.gpsTime
                        ? moment(truck.gpsTime).format("DD/MM/YYYY HH:mm:ss")
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TimeDiffDisplay
                      diffMinutes={truck.diffMinutes}
                      severity={truck.status.severity}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {truck.speed != null ? truck.speed : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Tooltip title={truck.address || "-"} placement="top-start">
                      <Typography variant="body2" noWrap>
                        {truck.address || "-"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAndSorted.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">ไม่พบข้อมูล</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  )
}

// ---- Sub Components ----

const SummaryCard = ({ title, subtitle, value, color, icon, compact }) => (
  <Card
    sx={{
      minWidth: compact ? 0 : 160,
      borderLeft: `4px solid ${color}`,
      borderRadius: 2,
    }}
    elevation={1}
  >
    <CardContent
      sx={{
        py: compact ? 1 : 1.5,
        px: compact ? 1.5 : 2,
        "&:last-child": { pb: compact ? 1 : 1.5 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            fontSize={compact ? "0.65rem" : undefined}
          >
            {title}
          </Typography>
          {subtitle && !compact && (
            <Typography
              variant="caption"
              display="block"
              color="text.disabled"
              fontSize="0.65rem"
            >
              {subtitle}
            </Typography>
          )}
          <Typography
            variant={compact ? "h5" : "h4"}
            fontWeight={700}
            sx={{ color }}
          >
            {value}
          </Typography>
        </Box>
        {!compact && (
          <Box sx={{ color, opacity: 0.3, fontSize: 40 }}>{icon}</Box>
        )}
      </Stack>
    </CardContent>
  </Card>
)

const MobileCard = ({ truck, index }) => (
  <Paper
    elevation={1}
    sx={{
      p: 1.5,
      borderRadius: 2,
      borderLeft: `4px solid ${
        truck.status.severity === 0
          ? "#2e7d32"
          : truck.status.severity === 1
            ? "#ed6c02"
            : truck.status.severity === 2
              ? "#d32f2f"
              : "#616161"
      }`,
      backgroundColor:
        truck.status.severity >= 3
          ? "rgba(211, 47, 47, 0.04)"
          : truck.status.severity === 2
            ? "rgba(211, 47, 47, 0.02)"
            : "#fff",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 0.5,
      }}
    >
      <Typography fontWeight={700} fontSize="1rem">
        {truck.vehicleName}
      </Typography>
      <Chip
        label={truck.status.label}
        color={truck.status.color}
        size="small"
        variant={truck.status.severity >= 2 ? "filled" : "outlined"}
        sx={{ fontWeight: 500 }}
      />
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 0.5,
        fontSize: "0.8rem",
        color: "text.secondary",
      }}
    >
      <Box>
        <Typography variant="caption" color="text.disabled">
          ห่างจากปัจจุบัน
        </Typography>
        <TimeDiffDisplay
          diffMinutes={truck.diffMinutes}
          severity={truck.status.severity}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.disabled">
          ความเร็ว
        </Typography>
        <Typography variant="body2">
          {truck.speed != null ? `${truck.speed} km/h` : "-"}
        </Typography>
      </Box>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <Typography variant="caption" color="text.disabled">
          GPS อัพเดตล่าสุด
        </Typography>
        <Typography variant="body2">
          {truck.gpsTime
            ? moment(truck.gpsTime).format("DD/MM/YYYY HH:mm:ss")
            : "-"}
        </Typography>
      </Box>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <Typography variant="caption" color="text.disabled">
          ตำแหน่ง
        </Typography>
        <Typography variant="body2" noWrap>
          {truck.address || "-"}
        </Typography>
      </Box>
    </Box>
  </Paper>
)

const headerCellStyle = {
  fontWeight: 700,
  backgroundColor: "#f5f5f5",
  whiteSpace: "nowrap",
}

const SortableHeader = ({ label, column, orderBy, order, onSort }) => (
  <TableCell sx={headerCellStyle}>
    <TableSortLabel
      active={orderBy === column}
      direction={orderBy === column ? order : "asc"}
      onClick={() => onSort(column)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
)

const TimeDiffDisplay = ({ diffMinutes, severity }) => {
  const text = formatTimeDiff(diffMinutes)
  const colorMap = {
    0: "success.main",
    1: "warning.main",
    2: "error.main",
    3: "text.disabled",
  }

  return (
    <Typography
      variant="body2"
      fontWeight={severity >= 2 ? 600 : 400}
      sx={{ color: colorMap[severity] || "text.primary" }}
    >
      {text}
    </Typography>
  )
}

export default GpsMonitor
